import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Check, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";


const fmt = (n) => `$${n.toFixed(2)}`;

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { state } = useLocation();
  const [order, setOrder] = useState(state?.order || null);
  const [loading, setLoading] = useState(!state?.order);

  useEffect(() => {
    if (order) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderNumber}`);
        if (mounted) setOrder(data);
      } catch (e) {
        // silently fail; show fallback
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [orderNumber, order]);

  const copyNumber = () => {
    navigator.clipboard.writeText(orderNumber);
    toast.success("Order number copied");
  };

  return (
    <div className="bg-shay-cream min-h-screen pt-32 pb-24 paper-noise relative" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
        <div className="inline-grid place-items-center h-20 w-20 rounded-full bg-shay-gold text-shay-navy mb-8">
          <Check size={36} strokeWidth={2.5} />
        </div>
        <p className="eyebrow">Order Confirmed</p>
        <h1 className="font-display text-5xl md:text-6xl text-shay-navy mt-4 tracking-tight">
          Sweet — we got it.
        </h1>
        <p className="mt-6 text-shay-chocolate/80 text-lg max-w-xl mx-auto leading-relaxed">
          Thanks{order?.customer?.first_name ? `, ${order.customer.first_name}` : ""}. We've sent a confirmation
          to your inbox. We'll start mixing as soon as the oven is warm.
        </p>

        <div className="mt-10 inline-flex items-center gap-3 bg-shay-navy text-shay-cream px-6 py-4">
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-shay-cream/60">Order Number</p>
            <p className="font-display text-2xl text-shay-gold tracking-wider" data-testid="confirmation-order-number">{orderNumber}</p>
          </div>
          <button onClick={copyNumber} className="ml-4 h-10 w-10 grid place-items-center border border-shay-gold/40 hover:bg-shay-gold hover:text-shay-navy transition-colors" data-testid="copy-order-number-btn" aria-label="Copy">
            <Copy size={16} />
          </button>
        </div>

        {loading && <p className="mt-10 text-shay-chocolate/60">Loading your order details...</p>}

        {order && (
          <div className="mt-12 text-left bg-white border border-shay-gold/20 p-8" data-testid="confirmation-details">
            <h2 className="font-display text-2xl text-shay-navy mb-6">What you ordered</h2>
            <ul className="space-y-4">
              {order.items.map((i) => (
                <li key={i.product_id} className="flex items-center gap-4">
                  <div className="h-14 w-14 shrink-0 bg-shay-cream-deep overflow-hidden">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-lg text-shay-navy">{i.name}</p>
                    <p className="text-xs uppercase tracking-widest text-shay-chocolate/60">Qty {i.quantity}</p>
                  </div>
                  <p className="text-shay-navy font-medium">{fmt(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>
            <dl className="mt-8 pt-6 border-t border-shay-gold/20 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-shay-chocolate/70">Subtotal</dt><dd>{fmt(order.subtotal)}</dd></div>
              {order.discount > 0 && <div className="flex justify-between text-shay-brown"><dt>Discount{order.promo_code ? ` (${order.promo_code})` : ""}</dt><dd>-{fmt(order.discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-shay-chocolate/70">Tax</dt><dd>{fmt(order.tax)}</dd></div>
              <div className="flex justify-between"><dt className="text-shay-chocolate/70">Delivery</dt><dd>{fmt(order.shipping)}</dd></div>
              <div className="flex justify-between pt-4 border-t border-shay-gold/20 font-display text-2xl text-shay-navy">
                <dt>Total</dt><dd>{fmt(order.total)}</dd>
              </div>
            </dl>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/track" state={{ orderNumber, email: order?.customer?.email }} className="btn-gold" data-testid="confirmation-track-btn">
            Track Order <ArrowRight size={16} />
          </Link>
          <Link to="/menu" className="btn-ghost-navy" data-testid="confirmation-keep-shopping-btn">
            Order More Treats
          </Link>
        </div>

        <p className="mt-12 text-[10px] uppercase tracking-widest text-shay-chocolate/60 max-w-2xl mx-auto leading-relaxed">
          <span className="text-shay-gold">Allergen Notice —</span> Baked in a home kitchen with wheat, dairy,
          eggs, soy, peanuts, and tree nuts. If you have a food allergy, please use caution.
        </p>
      </div>
    </div>
  );
}
